const en = {
  lang: 'en',
  langLabel: 'EN',
  langName: 'English',

  // ── Common ──────────────────────────────────────────────────────────────
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    back: 'Back',
    search: 'Search',
    loading: 'Loading…',
    submitting: 'Submitting…',
    saving: 'Saving…',
    confirm: 'Confirm',
    close: 'Close',
    yes: 'Yes',
    no: 'No',
    actions: 'Actions',
    status: 'Status',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    date: 'Date',
    notes: 'Notes',
    description: 'Description',
    total: 'Total',
    active: 'Active',
    inactive: 'Inactive',
    all: 'All',
    export: 'Export',
    import: 'Import',
    create: 'Create',
    update: 'Update',
    refresh: 'Refresh',
    logout: 'Logout',
    noData: 'No data found',
    error: 'An error occurred',
    success: 'Success',
    required: 'required',
    optional: 'optional',
    notAssigned: 'Not assigned',
    noneSelected: '-- Select --',
    history: 'History',
    view: 'View',
    details: 'Details',
    areYouSure: 'Are you sure?',
    cannotUndo: 'This action cannot be undone.',
    currency: 'UZS',
    month: 'Month',
    year: 'Year',
  },

  // ── Status labels ────────────────────────────────────────────────────────
  status: {
    ACTIVE: 'Active',
    INACTIVE: 'Inactive',
    PENDING: 'Pending',
    PAID: 'Paid',
    OVERDUE: 'Overdue',
    CANCELLED: 'Cancelled',
    COMPLETED: 'Completed',
    SCHEDULED: 'Scheduled',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    ON_LEAVE: 'On Leave',
    SUSPENDED: 'Suspended',
    TERMINATED: 'Terminated',
    RESIGNED: 'Resigned',
    PRESENT: 'Present',
    ABSENT: 'Absent',
    LATE: 'Late',
    EXCUSED: 'Excused',
  },

  // ── Navigation ───────────────────────────────────────────────────────────
  nav: {
    dashboard: 'Dashboard',
    students: 'Students',
    teachers: 'Teachers',
    groups: 'Groups',
    payments: 'Payments',
    expenditures: 'Expenditures',
    exams: 'Exams & Grades',
    certificates: 'Certificates',
    attendance: 'Attendance',
    homework: 'Homework',
    comments: 'Comments',
    telegram: 'Telegram',
    staff: 'Staff',
    messages: 'Messages',
    reports: 'Reports',
    centers: 'Centers',
    teacherSalaries: 'Teacher Salaries',
    printMaterials: 'Print Materials',
    logout: 'Logout',
    atRisk: 'At-Risk Students',
    marketIntel: 'Market Intel',
    competitors: 'Competitors',
    marketReport: 'Market Report',
  },

  // ── Auth / Login ─────────────────────────────────────────────────────────
  auth: {
    selectSchool: 'Select your school',
    selectSchoolSub: 'Choose your language center to continue',
    noSchool: "Don't see your school?",
    registerSchool: 'Register your school →',
    platformAdmin: 'Platform Admin Login',
    loginTitle: 'Welcome back',
    loginSub: 'Sign in to your account',
    email: 'Email',
    password: 'Password',
    signIn: 'Sign In',
    forgotPassword: 'Forgot password?',
    noAccount: "Don't have an account?",
    signUp: 'Sign up',
    backToLogin: '← Back to login',
    changeCenter: 'Change',
    registerTitle: 'Register your school',
    registerSub: 'Set up your School CRM account in minutes',
    newSchool: 'New School',
    joinExisting: 'Join Existing',
    firstName: 'First Name',
    lastName: 'Last Name',
    centerName: 'School Name',
    centerCode: 'School Code',
    centerAddress: 'Address',
    brandColor: 'Brand Color',
    confirmPassword: 'Confirm Password',
    passwordMismatch: 'Passwords do not match',
    passwordTooShort: 'Password must be at least 6 characters',
    createAccount: 'Create Account',
    verifyTitle: 'Check your email',
    verifyCode: 'Enter the 6-digit code we sent to',
    resend: 'Resend code',
    verify: 'Verify',
    forgotTitle: 'Forgot password?',
    forgotSub: "No worries, we'll send you a 6-digit reset code.",
    sendCode: 'Send Reset Code',
    resetTitle: 'Enter reset code',
    resetSub: 'We sent a reset code to',
    newPasswordTitle: 'Set new password',
    newPasswordSub: 'Choose a strong password for your account.',
    newPassword: 'New Password',
    setPassword: 'Set Password',
    doneTitle: 'Password reset!',
    doneSub: 'Your password has been updated successfully. You can now sign in with your new password.',
    signInNow: 'Sign In',
    platformTitle: 'Platform Admin',
    platformSub: 'Restricted access — authorised personnel only',
    signInPlatform: 'Sign In as Platform Admin',
    wrongRole: 'This login is for platform administrators only',
    strengthWeak: 'Weak',
    strengthFair: 'Fair',
    strengthStrong: 'Strong',
    resendIn: 'Resend in',
    seconds: 's',
    codeExpired: 'Code expired?',
    backToSignup: '← Back to sign up',
  },

  // ── Dashboard ────────────────────────────────────────────────────────────
  dashboard: {
    title: '📊 Dashboard',
    activeStudents: 'Active Students',
    teachers: 'Teachers',
    groups: 'Groups',
    studentsPaid: 'Students Paid',
    studentsPending: 'Students Pending',
    studentsOverdue: 'Students Overdue',
    present: 'Present',
    absent: 'Absent',
    late: 'Late',
    paymentStatus: '💰 Payment Status',
    attendanceMonth: '📝 Attendance Today',
    alerts: '🚨 Preparation Alerts',
    quickActions: 'Quick Actions',
    systemStatus: 'System Status',
    paidThisMonth: 'paid this month',
    awaitingPayment: 'awaiting payment',
    overdueMonth: 'overdue this month',
    totalInDB: 'total in DB',
    activeInstructors: 'Active instructors',
    allActiveGroups: 'All active groups',
    records: 'records',
    addStudent: '➕ Add Student',
    addTeacher: '➕ Add Teacher',
    createGroup: '➕ Create Group',
    dismissAll: 'Dismiss all',
    loadingAlerts: 'Loading alerts…',
    activeOf: 'active of',
    activeGroups: 'active groups running',
    paid: 'paid',
    unpaid: 'unpaid',
  },

  // ── Students ─────────────────────────────────────────────────────────────
  students: {
    title: '👥 Students',
    addStudent: '➕ Add Student',
    deleteAll: '🗑️ Delete All',
    exportCsv: '📥 Export CSV',
    importStudents: '📤 Import Students',
    searchPlaceholder: 'Search by name or student ID…',
    totalStudents: 'Total Students',
    showing: 'Showing',
    results: 'results',
    noStudents: 'No students found matching your search',
    noStudentsYet: 'No students yet. Import students to get started.',
    failedLoad: '❌ Failed to load students',
    deleted: '✅ Student deleted successfully',
    failedDelete: '❌ Failed to delete student',
    errorDelete: '❌ Error deleting student',
    confirmDelete: 'Are you sure you want to delete this student?',
    confirmDeleteAll: '⚠️ WARNING: This will delete ALL students and parents. Are you absolutely sure?',
    typeToConfirm: 'Type "DELETE ALL" to confirm.',
    studentId: 'Student ID',
    groups: 'Groups',
    groupCount: 'group(s)',
  },

  // ── Teachers ─────────────────────────────────────────────────────────────
  teachers: {
    title: '👨‍🏫 Teachers Management',
    subtitle: 'Manage all teachers and their assignments',
    addTeacher: '➕ Add Teacher',
    searchPlaceholder: '🔍 Search teachers…',
    noTeachers: 'No teachers found',
    tryAdjusting: 'Try adjusting your search or filters',
    confirmDelete: 'Are you sure you want to delete this teacher?',
    specialization: '🎯 Specialization',
    editTeacher: '✏️ Edit Teacher',
    firstName: 'First Name',
    lastName: 'Last Name',
    password: 'Password',
    updateTeacher: 'Update Teacher',
  },

  // ── Groups ───────────────────────────────────────────────────────────────
  groups: {
    title: '📚 Groups',
    addGroup: '➕ Add Group',
    editGroup: 'Edit Group',
    addNewGroup: 'Add New Group',
    searchPlaceholder: 'Search by name or group code…',
    totalGroups: 'Total Groups',
    groupName: 'Group Name',
    groupCode: 'Group Code',
    level: 'Level',
    maxStudents: 'Max Students',
    teacher: 'Teacher',
    schedule: 'Schedule',
    noTeacher: '-- No Teacher --',
    createGroup: 'Create Group',
    updateGroup: 'Update Group',
    manageStudents: '👥 Manage Students',
    enrolled: 'Enrolled',
    available: 'Available',
    searchEnrolled: 'Search enrolled…',
    searchAvailable: 'Search available…',
    remove: 'Remove',
    noStudentsEnrolled: 'No students enrolled yet',
    allEnrolled: 'All students are enrolled',
    noHistory: 'No history found.',
    historyNote: 'Full enrollment history for this group — all students who were ever added or removed.',
    added: 'Added',
    removed: 'Removed',
    students: 'Students',
    sessions: 'Sessions',
    noGroups: 'No groups found',
    confirmDeleteGroup: 'Are you sure you want to delete this group?',
    confirmRemoveStudent: 'Remove this student from the group?',
    levels: {
      A1: 'A1 - Beginner',
      A2: 'A2 - Elementary',
      B1: 'B1 - Intermediate',
      B2: 'B2 - Upper Intermediate',
      C1: 'C1 - Advanced',
      C2: 'C2 - Proficiency',
    },
  },

  // ── Attendance ───────────────────────────────────────────────────────────
  attendance: {
    title: '📊 Attendance Overview',
    subtitle: 'All groups · attendance statistics',
    totalSessions: 'Total Sessions',
    overallRate: 'Overall Rate',
    searchPlaceholder: 'Search by group name, code, or teacher…',
    sortBy: 'Sort:',
    byName: 'Name',
    byRate: 'Rate',
    bySessions: 'Sessions',
    byStudents: 'Students',
    noSessions: 'No sessions',
    noGroups: 'No groups match your search.',
    viewAbsent: '🔴 View Absent',
    absentStudents: '🔴 Absent Students',
    addComment: 'Add a comment for this student…',
    noAbsent: 'No absent students in this session.',
    loadingSessions: 'Loading sessions…',
    noSessionsRecorded: 'No sessions recorded yet.',
    rate: 'Rate',
    lastSession: 'Last Session',
    attendanceRate: 'Attendance Rate',
    markAttendance: 'Mark Attendance',
    present: 'Present',
    absent: 'Absent',
    late: 'Late',
    excused: 'Excused',
    submit: 'Submit Attendance',
  },

  // ── Payments ─────────────────────────────────────────────────────────────
  payments: {
    title: '💰 Payment Management',
    addPayment: 'Add Payment',
    amount: 'Amount',
    method: 'Method',
    dueDate: 'Due Date',
    paidAt: 'Paid At',
    invoiceNumber: 'Invoice #',
    methods: {
      CASH: 'Cash',
      CARD: 'Card',
      TRANSFER: 'Transfer',
      ONLINE: 'Online',
    },
    statuses: {
      PAID: 'Paid',
      PENDING: 'Pending',
      OVERDUE: 'Overdue',
      CANCELLED: 'Cancelled',
    },
    markPaid: 'Mark as Paid',
    passcodeRequired: 'Passcode required',
    incorrectPasscode: 'Incorrect passcode',
    monthlyRevenue: 'Monthly Revenue',
    collected: 'Collected',
    expected: 'Expected',
  },

  // ── Expenditures ─────────────────────────────────────────────────────────
  expenditures: {
    title: '📉 Expenditures',
    addExpenditure: 'Add Expenditure',
    category: 'Category',
    categories: {
      ALL: 'All',
      RENT: 'Rent',
      UTILITIES: 'Utilities',
      SALARIES: 'Salaries',
      SUPPLIES: 'Supplies',
      EQUIPMENT: 'Equipment',
      MARKETING: 'Marketing',
      MAINTENANCE: 'Maintenance',
      CLEANERS: 'Cleaners',
      OTHER: 'Other',
    },
    confirmDelete: 'Delete this expenditure?',
    title_field: 'Title',
    totalSpent: 'Total Spent',
    dailyTotal: 'Daily Total',
  },

  // ── Exams ────────────────────────────────────────────────────────────────
  exams: {
    title: '📋 Exams & Grades',
    addExam: 'Add Exam',
    examTitle: 'Exam Title',
    subject: 'Subject',
    type: 'Type',
    group: 'Group',
    maxScore: 'Max Score',
    passingScore: 'Passing Score',
    gradeEntry: 'Grade Entry',
    saveGrades: 'Save Grades',
    gradesSaved: 'Grades saved!',
    avgScore: 'Average Score',
    passRate: 'Pass Rate',
    confirmDelete: 'Delete this exam and all its results?',
    types: {
      WRITTEN: 'Written',
      ORAL: 'Oral',
      PRACTICAL: 'Practical',
      ONLINE: 'Online',
      QUIZ: 'Quiz',
    },
    statuses: {
      SCHEDULED: 'Scheduled',
      COMPLETED: 'Completed',
      CANCELLED: 'Cancelled',
    },
    score: 'Score',
    absent: 'Absent',
    participant: 'Participants',
  },

  // ── Comments ─────────────────────────────────────────────────────────────
  comments: {
    title: '💬 Student Feedback',
    addFeedback: 'Add Feedback',
    selectGroup: 'Select Group',
    selectStudent: 'Select Student',
    quickComments: 'Quick Comments',
    commentPlaceholder: 'Write your feedback about the student…',
    addComment: '💬 Add Comment',
    recentFeedback: 'Recent Feedback',
    noComments: 'No recent comments',
    by: 'By',
    failedLoad: '❌ Failed to load',
    added: '✅ Comment added successfully!',
    failedAdd: '❌ Failed to add comment',
  },

  // ── Homework ─────────────────────────────────────────────────────────────
  homework: {
    title: '📓 Homework',
    addHomework: 'Add Homework',
    dueDate: 'Due Date',
    points: 'Points',
    type: 'Type',
    submissions: 'Submissions',
    results: 'Results',
    confirmDelete: 'Are you sure you want to delete this homework?',
    noHomework: 'No homework found',
    titleRequired: '❌ Title and due date are required',
    created: '✅ Homework created successfully!',
    deleted: '✅ Homework deleted',
  },

  // ── Staff ────────────────────────────────────────────────────────────────
  staff: {
    title: '👔 Staff Management',
    addStaff: 'Add Staff',
    employeeId: 'Employee ID',
    position: 'Position',
    department: 'Department',
    employmentType: 'Employment Type',
    hireDate: 'Hire Date',
    baseSalary: 'Base Salary',
    paymentFrequency: 'Payment Frequency',
    emergencyContact: 'Emergency Contact',
    types: {
      FULL_TIME: 'Full Time',
      PART_TIME: 'Part Time',
      CONTRACT: 'Contract',
    },
  },

  // ── Messages ─────────────────────────────────────────────────────────────
  messages: {
    title: '📨 Messages',
    typeMessage: 'Type a message…',
    send: 'Send',
    teachers: 'Teachers',
    students: 'Students',
    parents: 'Parents',
    admin: 'Admin',
    noConversation: 'Select a conversation to start messaging',
  },

  // ── Teacher Portal ───────────────────────────────────────────────────────
  teacherPortal: {
    title: '🎓 Teacher Portal',
    home: '🏠 Home',
    myGroups: '📚 My Groups',
    attendance: '📝 Attendance',
    homework: '📓 Homework',
    exams: '📋 Exams & Grades',
    comments: '💬 Student Comments',
    logout: '🚪 Logout',
    welcome: 'Welcome back',
    todayOverview: "Here's your overview for today.",
    myGroupsLabel: 'My Groups',
    totalStudents: 'Total Students',
    today: 'Today',
    noGroups: 'No groups assigned yet.',
    attendanceBtn: 'Attendance',
    gradesBtn: 'Grades',
    submitAttendance: 'Submit Attendance',
    saving: 'Saving…',
    commentAdded: '✅ Comment added successfully',
    addComment: 'Add Comment',
    selectStudent: 'Select Student',
    commentText: 'Comment',
    commentPlaceholder: 'Write your comment here…',
  },

  // ── Parent Portal ────────────────────────────────────────────────────────
  parentPortal: {
    title: '👨‍👩‍👧 Parent Portal',
    myChild: 'My Child',
    attendance: 'Attendance',
    payments: 'Payments',
    comments: 'Comments',
    groups: 'My Groups',
    attendanceCalendar: 'Attendance Calendar',
    present: 'Present',
    absent: 'Absent',
    late: 'Late',
    excused: 'Excused',
    noData: 'No data for this period',
    recentPayments: 'Recent Payments',
    recentComments: 'Recent Comments',
    noPayments: 'No payment records',
    noComments: 'No comments yet',
    level: 'Level',
    teacher: 'Teacher',
    schedule: 'Schedule',
    days: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  },

  // ── Master Admin ─────────────────────────────────────────────────────────
  master: {
    title: 'Master Dashboard',
    subtitle: 'Monitor and manage all schools on the platform',
    totalSchools: 'Total Schools',
    totalStudents: 'Total Students',
    totalTeachers: 'Total Teachers',
    monthlyRevenue: 'Revenue This Month',
    searchPlaceholder: 'Search schools by name or code…',
    viewDetails: 'View Details',
    disable: 'Disable',
    enable: 'Enable',
    resetPw: 'Reset PW',
    resetPassword: 'Reset Password',
    newPassword: 'New password (min 6 chars)',
    lastActivity: 'Last activity',
    avgExamScore: 'Avg exam score',
    never: 'Never',
    backToSchools: '← Back to all schools',
    monthlyRevenueLabel: 'Monthly Revenue',
    newStudents: 'New Students',
    recentExams: 'Recent Exams',
    paidThisMonth: 'Paid (this month)',
    pending: 'Pending',
    overdue: 'Overdue',
    signOut: 'Sign out',
    platformLabel: 'MASTER',
    revenue: 'Revenue',
    thisMonth: 'this month',
    loadingPlatform: 'Loading platform data…',
    failedLoad: 'Failed to load platform data',
    examCol: 'Exam',
    groupCol: 'Group',
    dateCol: 'Date',
    avgScoreCol: 'Avg Score',
    passRateCol: 'Pass Rate',
    studentsCol: 'Students',
  },

  // ── Centers ──────────────────────────────────────────────────────────────
  centers: {
    title: '🏢 Language Centers',
    subtitle: "Manage your organization's centers. Each center has its own login.",
    addCenter: '+ Add Center',
    newCenter: 'New Center',
    editCenter: 'Edit Center',
    centerName: 'Center Name',
    code: 'Code',
    codeHint: '(unique slug, no spaces)',
    logoUrl: 'Logo URL',
    logoOptional: '(optional)',
    brandColor: 'Brand Color',
    preview: 'Preview',
    created: '✅ Center created',
    updated: '✅ Center updated',
  },
  certificates: {
    title: 'Certificates',
    addCertificate: 'Add Certificate',
    editCertificate: 'Edit Certificate',
    student: 'Student',
    teacher: 'Teacher',
    type: 'Type',
    examDate: 'Exam Date',
    issueDate: 'Issue Date',
    result: 'Result',
    notes: 'Notes',
    typeIELTS: 'IELTS',
    typeCEFR: 'CEFR',
    typeCUSTOM: 'Custom',
    ieltsOverall: 'Overall Band',
    ieltsListening: 'Listening',
    ieltsReading: 'Reading',
    ieltsWriting: 'Writing',
    ieltsSpeaking: 'Speaking',
    cefrLevel: 'CEFR Level',
    customName: 'Certificate Name',
    customResult: 'Result / Score',
    statistics: 'Statistics',
    totalCertificates: 'Total Certificates',
    byTeacher: 'By Teacher',
    avgBand: 'Avg IELTS Band',
    ieltsDistribution: 'IELTS Band Distribution',
    cefrDistribution: 'CEFR Level Distribution',
    monthlyTrend: 'Monthly Trend',
    exportExcel: 'Export to Excel',
    annual: 'Annual',
    monthly: 'Monthly',
    noCertificates: 'No certificates found',
    confirmDelete: 'Are you sure you want to delete this certificate?',
    saved: 'Certificate saved successfully',
    deleted: 'Certificate deleted',
    list: 'Certificates',
    certificateFile: 'Certificate File',
    viewFile: 'View',
    uploadFile: 'Upload PDF or image (max 10 MB)',
  },

  aiAssistant: {
    title: 'AI Assistant',
    placeholder: 'Ask about students, payments, attendance...',
    send: 'Send',
    close: 'Close',
    error: 'Something went wrong. Please try again.',
    thinking: 'Thinking...',
    welcome: 'Hi! I can help you with questions about your school data. Try asking:',
    suggestion1: 'Who has unpaid fees this month?',
    suggestion2: 'Which students are frequently absent?',
    suggestion3: 'Show me revenue for this month',
    suggestion4: 'What are the active groups?',
    suggestion5: 'Give me overall school statistics',
  },

  // ── Market Intelligence ───────────────────────────────────────────────────
  market: {
    title: 'Market Intelligence',
    competitors: 'Competitors',
    marketReport: 'Market Report',
    threatLevel: 'Threat Level',
    bcPartner: 'BC Partner',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    addCompetitor: 'Add Competitor',
    editCompetitor: 'Edit Competitor',
    deactivate: 'Deactivate',
    history: 'History',
    noHistory: 'No history yet.',
    consideredBy: 'Considered by',
    studentsLost: 'Students lost',
    lastChecked: 'Last checked',
    leadsBySource: 'Leads & Enrolments by Source',
    competitorsConsidered: 'Competitors Considered',
    lossReasons: 'Loss Reasons',
    competitorLosses: 'Students Lost to Competitors',
    source: 'How they found us',
    whyChoseUs: 'Why they chose us',
    homeDistrict: 'Home District',
    leavingReason: 'Reason for leaving',
    wentToCompetitor: 'Went to competitor',
    whichCompetitor: 'Which competitor?',
    // source options
    sourceTelegram: 'Telegram',
    sourceInstagram: 'Instagram',
    sourceFriendFamily: 'Friend or family',
    sourceCurrentStudent: 'Current student',
    source2GIS: '2GIS / Yandex Maps',
    sourceFlyerBanner: 'Flyer or banner',
    sourceSchoolEvent: 'School event',
    sourceWalkIn: 'Walk-in',
    sourceOther: 'Other',
    // why chose us
    whyPrice: 'Price',
    whyLocation: 'Location',
    whyTeacher: 'Teacher',
    whyResults: 'Results',
    whySchedule: 'Schedule',
    whyRecommendation: 'Recommendation',
    // leaving reasons
    reasonPrice: 'Price',
    reasonSchedule: 'Schedule',
    reasonDistance: 'Distance',
    reasonWentToCompetitor: 'Went to competitor',
    reasonTeacher: 'Teacher',
    reasonResults: 'Results',
    reasonMovedAway: 'Moved away',
    reasonNoReply: 'No reply',
    reasonOther: 'Other',
  },
};

export default en;
